import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { settingsService } from '../services/settings.service';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/authStore';
import type { PasswordChangeRequest, PasswordChangeHistory } from '../types/settings.types';
import { useErrorStore } from '@/stores/errorStore';
import { usePasswordPolicy } from '@/hooks/usePasswordPolicy';
import { passwordProblem, passwordRuleText } from '@/utils/accountRules';
import { useFieldErrors, type FieldErrors } from '@/utils/formErrors';

// QA #68: which input a change-password refusal belongs to. Keys are the
// request's JSON fields (old_password / new_password); confirm_password is
// the form's own.
const CHANGE_PASSWORD_FIELDS: Record<string, string> = {
  INVALID_OLD_PASSWORD: 'old_password',
  PASSWORD_TOO_SHORT: 'new_password',
  PASSWORD_TOO_SIMPLE: 'new_password',
  PASSWORD_CONTAINS_USERNAME: 'new_password',
  PASSWORD_SAME_AS_OLD: 'new_password',
};

export default function Password() {
  const showSuccess = useErrorStore((s) => s.showSuccess);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const storeLogout = useAuthStore((s) => s.logout);
  // QA #63 / #67: the minimum length and expiry the backend enforces.
  const policy = usePasswordPolicy();
  const [formData, setFormData] = useState<PasswordChangeRequest>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [history, setHistory] = useState<PasswordChangeHistory[]>([]);
  // `error` is only for problems that belong to no field (server down …);
  // field problems are marked on the input (QA #68).
  const [error, setError] = useState('');
  const form = useFieldErrors();
  const mfaForm = useFieldErrors();

  // MFA settings state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  // Assume mail works until the status says otherwise, so the warning never
  // flashes on load.
  const [smtpAvailable, setSmtpAvailable] = useState(true);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaStatusLoading, setMfaStatusLoading] = useState(true);
  const [mfaSuccess, setMfaSuccess] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    loadHistory();
    loadMfaStatus();
  }, []);

  const loadHistory = async () => {
    try {
      const data = await settingsService.getPasswordHistory();
      setHistory(data);
    } catch (error) {
      console.error('Failed to load password history:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 驗證 (backend internal/passwordpolicy, QA #63 / #66) — every problem
    // is marked on its own field at once (QA #68).
    const problems: FieldErrors = {};
    if (!formData.oldPassword) {
      problems.old_password = '請輸入目前使用的密碼';
    }
    const problem = passwordProblem(formData.newPassword, policy, user?.username);
    if (problem) {
      problems.new_password = problem;
    } else if (formData.newPassword === formData.oldPassword) {
      problems.new_password = '新密碼不可與目前的密碼相同，請換一組';
    }
    if (formData.newPassword !== formData.confirmPassword) {
      problems.confirm_password = '兩次輸入的新密碼不一致，請再輸入一次相同的新密碼';
    }
    if (Object.keys(problems).length > 0) {
      form.setErrors(problems);
      return;
    }
    form.clear();

    try {
      await settingsService.changePassword(formData);
      // ADR-046 / QA #17: the server bumped the token epoch, so this session
      // (and every other one of this account) is already dead. Sign out
      // cleanly and ask for a fresh login instead of letting the next request
      // bounce with TOKEN_REVOKED.
      showSuccess('密碼已變更，請重新登入');
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      storeLogout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to change password:', error);
      // skipGlobalError rejects with a plain Error carrying .code / .fields
      // (api.ts). A wrong current password is INVALID_OLD_PASSWORD (400),
      // shown under that field — the user is no longer logged out (QA #16);
      // PASSWORD_* name the configured rule (QA #63 / #66) under 新密碼.
      setError(form.apply(error, CHANGE_PASSWORD_FIELDS, '密碼修改失敗，請稍後再試'));
    }
  };

  const handleCancel = () => {
    setFormData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setError('');
    form.clear();
  };

  // MFA functions
  const loadMfaStatus = async () => {
    try {
      setMfaStatusLoading(true);
      const status = await authService.getMfaStatus();
      setMfaEnabled(status.mfa_enabled);
      setSmtpAvailable(status.smtp_available ?? true);
    } catch (err) {
      console.error('Failed to load MFA status:', err);
    } finally {
      setMfaStatusLoading(false);
    }
  };

  const handleMfaToggle = async () => {
    setMfaSuccess('');
    setMfaError('');

    if (mfaEnabled) {
      // Open dialog to confirm with password
      setDisableDialogOpen(true);
    } else {
      // Enable MFA directly
      setMfaLoading(true);
      try {
        await authService.enableMfa();
        setMfaEnabled(true);
        setMfaSuccess('MFA 已啟用，下次登入時將需要輸入 Email 驗證碼');
      } catch (err) {
        setMfaError(err instanceof Error ? err.message : '啟用 MFA 失敗');
      } finally {
        setMfaLoading(false);
      }
    }
  };

  const handleDisableMfa = async () => {
    if (!disablePassword) {
      mfaForm.setField('password', '請輸入目前使用的密碼以確認停用');
      return;
    }

    setMfaLoading(true);
    setMfaError('');
    mfaForm.clear();
    try {
      await authService.disableMfa(disablePassword);
      setMfaEnabled(false);
      setDisableDialogOpen(false);
      setDisablePassword('');
      setMfaSuccess('MFA 已停用');
    } catch (err) {
      // A wrong password is the password field's problem (QA #68).
      setMfaError(mfaForm.apply(err, { INVALID_CREDENTIALS: 'password' }, '停用 MFA 失敗，請稍後再試'));
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableDialogClose = () => {
    setDisableDialogOpen(false);
    setDisablePassword('');
    setMfaError('');
    mfaForm.clear();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        密碼修改
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        變更您的登入密碼
      </Typography>

      {user?.must_change_password && (
        <Alert severity="warning" sx={{ mt: 2, maxWidth: 600 }}>
          <Typography variant="body2" fontWeight={600}>
            {user.password_expired
              ? `您的密碼已超過 ${policy.maxAgeDays} 天未更換，請設定新密碼後再繼續使用系統。`
              : '您必須修改密碼才能繼續使用系統。'}
          </Typography>
        </Alert>
      )}

      {/* noValidate: the form's own Chinese hints replace the browser's required bubble (QA #68) */}
      <Paper sx={{ p: 3, mt: 3, maxWidth: 600 }} component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="h6" gutterBottom>
          修改密碼
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          type="password"
          label="目前密碼"
          autoComplete="current-password"
          value={formData.oldPassword}
          onChange={(e) => setFormData({ ...formData, oldPassword: e.target.value })}
          required
          margin="normal"
          {...form.bind('old_password')}
        />

        <TextField
          fullWidth
          type="password"
          label="新密碼"
          autoComplete="new-password"
          value={formData.newPassword}
          onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
          required
          margin="normal"
          {...form.bind('new_password', `${passwordRuleText(policy)}，不可與目前的密碼相同`)}
        />

        <TextField
          fullWidth
          type="password"
          label="確認新密碼"
          autoComplete="new-password"
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          required
          margin="normal"
          {...form.bind('confirm_password')}
        />

        <Alert icon={<WarningIcon />} severity="warning" sx={{ mt: 3, mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            安全提醒：
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>請使用強密碼保護您的帳戶</li>
            <li>不要與他人分享您的密碼</li>
            <li>定期更換密碼以確保安全</li>
          </Box>
        </Alert>

        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            確認修改
          </Button>
          <Button variant="outlined" onClick={handleCancel}>
            取消
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mt: 3, maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom>
          密碼修改記錄
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>修改時間</TableCell>
                <TableCell>IP 位址</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>{record.changeTime}</TableCell>
                  <TableCell>{record.ipAddress}</TableCell>
                </TableRow>
              ))}
              {history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    暫無修改記錄
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* MFA Settings */}
      <Paper sx={{ p: 3, mt: 3, maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom>
          兩步驟驗證 (MFA)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          啟用後，每次登入時將發送驗證碼到您的 Email 信箱
        </Typography>

        {mfaSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMfaSuccess('')}>
            {mfaSuccess}
          </Alert>
        )}

        {mfaError && !disableDialogOpen && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMfaError('')}>
            {mfaError}
          </Alert>
        )}

        {/* QA #33: enabling MFA with no SMTP means no code can ever arrive —
            the next login would fail. Say so and keep the switch off. */}
        {!mfaStatusLoading && !smtpAvailable && !mfaEnabled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            系統尚未設定郵件伺服器（SMTP），暫時無法啟用 Email 驗證。請聯絡管理員確認部署環境的寄信設定。
          </Alert>
        )}
        {!mfaStatusLoading && !smtpAvailable && mfaEnabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            目前無法寄送驗證碼：郵件伺服器（SMTP）未設定，下次登入將無法完成驗證。建議先停用 MFA，等 SMTP 就緒再開啟。
          </Alert>
        )}

        {mfaStatusLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">載入中...</Typography>
          </Box>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={mfaEnabled}
                onChange={handleMfaToggle}
                disabled={mfaLoading || (!mfaEnabled && !smtpAvailable)}
              />
            }
            label="Email MFA 驗證"
          />
        )}
      </Paper>

      {/* Disable MFA confirmation dialog */}
      <Dialog open={disableDialogOpen} onClose={handleDisableDialogClose}>
        <DialogTitle>停用 MFA 驗證</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            停用 MFA 驗證將降低帳號安全性，請輸入密碼確認。
          </Typography>
          {mfaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mfaError}
            </Alert>
          )}
          <TextField
            fullWidth
            type="password"
            label="密碼"
            autoComplete="current-password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            autoFocus
            disabled={mfaLoading}
            {...mfaForm.bind('password')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisableDialogClose} disabled={mfaLoading}>
            取消
          </Button>
          <Button
            onClick={handleDisableMfa}
            color="error"
            variant="contained"
            disabled={mfaLoading || !disablePassword}
          >
            {mfaLoading ? <CircularProgress size={20} /> : '確認停用'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
